import { DSA_FRONT_ORIGIN } from '@/utils/payment'
import { getInstitutionAccountId, getInstitutionAdvisorId } from '@/utils/store'

/** Legacy EAE host — non-DSA programs only. */
export const EAE_REGISTRATION_ORIGIN = process.env.VUE_APP_EAE_FRONT_URL || 'https://my.eae.international'

/** Institution-specific DSA registration forms are hosted in the EAE form app. */
export const DSA_REGISTRATION_ORIGIN = (
  process.env.VUE_APP_DSA_REG_URL
  || EAE_REGISTRATION_ORIGIN
).replace(/\/$/, '')

/** Institution-specific student registration form (pending payment + account binding). */
export function buildInstitutionRegistrationUrl({ accountId, projectCode, contactId = '', advisorId = '' } = {}) {
  const account_id = accountId || getInstitutionAccountId()
  const project_code = String(projectCode || '').trim()
  if (!account_id || !project_code) return ''
  // The external form binds the student to this institution and subject/project.
  let url = `${DSA_REGISTRATION_ORIGIN}/#/form/registration?account_id=${encodeURIComponent(account_id)}&project_code=${encodeURIComponent(project_code)}`
  const advisor = String(contactId || advisorId || getInstitutionAdvisorId() || '').trim()
  if (advisor) {
    // EAE CommonStudentForm reads contact_id; also pass advisor_id for clarity/attribution.
    url += `&contact_id=${encodeURIComponent(advisor)}&advisor_id=${encodeURIComponent(advisor)}`
  }
  return url
}

/**
 * Public shareable poster page — slim query only:
 * account_id, project_code, advisor_id (optional).
 * Poster content is loaded from the API.
 */
export function buildInstitutionPosterUrl(params = {}) {
  const q = new URLSearchParams()
  const account_id = params.account_id || getInstitutionAccountId() || ''
  const project_code = params.project_code || ''
  const advisor_id = params.advisor_id || getInstitutionAdvisorId() || ''
  if (account_id) q.set('account_id', String(account_id).trim())
  if (project_code) q.set('project_code', String(project_code).trim())
  if (advisor_id) q.set('advisor_id', String(advisor_id).trim())
  const origin = (DSA_FRONT_ORIGIN || '').replace(/\/$/, '') || window.location.origin
  return `${origin}/#/poster?${q.toString()}`
}

export function copyTextToClipboard(text) {
  if (!text) return Promise.reject(new Error('empty'))
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  return new Promise((resolve, reject) => {
    const input = document.createElement('textarea')
    input.value = text
    input.setAttribute('readonly', '')
    input.style.position = 'fixed'
    input.style.left = '-9999px'
    document.body.appendChild(input)
    input.select()
    try {
      const ok = document.execCommand('copy')
      document.body.removeChild(input)
      if (ok) resolve()
      else reject(new Error('copy failed'))
    } catch (e) {
      document.body.removeChild(input)
      reject(e)
    }
  })
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(s)
  })
}

export function ensureQrCodeLib() {
  if (window.QRCode) return Promise.resolve()
  return loadScript('https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js')
    .catch(() => loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'))
}

export function ensureHtml2Canvas() {
  if (window.html2canvas) return Promise.resolve()
  return loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
}
